import { css } from 'lit';
import { hostReset } from '../styles/mixins.js';
import * as t from '../styles/tokens.js';

export default [
  hostReset,
  css`
    :host { display: contents; }

    /* ══════════════════════════════════════════════════════════════════════
       Keyframes — slide from each edge
    ══════════════════════════════════════════════════════════════════════ */
    @keyframes uwc-drw-in-left    { from { transform: translateX(-100%); } to { transform: translateX(0); } }
    @keyframes uwc-drw-out-left   { from { transform: translateX(0); }     to { transform: translateX(-100%); } }
    @keyframes uwc-drw-in-right   { from { transform: translateX(100%); }  to { transform: translateX(0); } }
    @keyframes uwc-drw-out-right  { from { transform: translateX(0); }     to { transform: translateX(100%); } }
    @keyframes uwc-drw-in-top     { from { transform: translateY(-100%); } to { transform: translateY(0); } }
    @keyframes uwc-drw-out-top    { from { transform: translateY(0); }     to { transform: translateY(-100%); } }
    @keyframes uwc-drw-in-bottom  { from { transform: translateY(100%); }  to { transform: translateY(0); } }
    @keyframes uwc-drw-out-bottom { from { transform: translateY(0); }     to { transform: translateY(100%); } }

    @keyframes uwc-drw-bd-in  { from { opacity: 0; } to { opacity: 1; } }
    @keyframes uwc-drw-bd-out { from { opacity: 1; } to { opacity: 0; } }

    /* ══════════════════════════════════════════════════════════════════════
       Native <dialog> resets + base layout
       NOTE: inset / margin / width / height are applied via inline styles
       in _applyPosition() to beat the browser's UA stylesheet, which sets
       margin:auto and max-height/max-width constraints that CSS classes
       alone cannot override (same pattern as uwc-dialog).
    ══════════════════════════════════════════════════════════════════════ */
    dialog.uwc-drw {
      /* Reset UA */
      border:     none;
      padding:    0;
      outline:    none;
      color:      inherit;
      overflow:   hidden;

      /* UA also sets position:absolute; keep fixed for top-layer pinning */
      position:   fixed;

      /* Visual */
      background: var(--uwc-drawer-bg,    ${t.surface});
      border:     var(--uwc-drawer-border, 1px solid ${t.border});
      box-shadow: var(--uwc-drawer-shadow, ${t.shadowLg});

      /* Flex column: header → scrollable content → footer */
      display:        flex;
      flex-direction: column;
    }

    dialog.uwc-drw:not([open]) { display: none; }

    /* ── Backdrop ─────────────────────────────────────────────────────────── */
    dialog[open].uwc-drw::backdrop {
      background: var(--uwc-drawer-backdrop, var(--uwc-backdrop-color, rgba(0, 0, 0, 0.45)));
      animation:  uwc-drw-bd-in var(--uwc-drawer-duration, ${t.durationBase}) ease both;
    }
    dialog[open].uwc-drw.is-closing::backdrop {
      animation: uwc-drw-bd-out var(--uwc-drawer-duration, ${t.durationBase}) ease both;
    }
    dialog.uwc-drw--no-modal::backdrop { background: transparent; }

    /* ══════════════════════════════════════════════════════════════════════
       Position variants
       Only visual properties (border-radius, edge-border removal, animation)
       live here. Geometry (inset, margin, width, height) is set inline in JS.
    ══════════════════════════════════════════════════════════════════════ */

    /* ── Left ── */
    dialog.uwc-drw--left {
      border-radius: 0 var(--uwc-drawer-radius, ${t.radiusLg}) var(--uwc-drawer-radius, ${t.radiusLg}) 0;
      border-left:   none;
    }
    dialog[open].uwc-drw--left:not(.is-closing) {
      animation: uwc-drw-in-left var(--uwc-drawer-duration, ${t.durationBase}) var(--uwc-easing, ${t.easingStandard}) both;
    }
    dialog[open].uwc-drw--left.is-closing {
      animation: uwc-drw-out-left var(--uwc-drawer-duration, ${t.durationBase}) ease both;
    }

    /* ── Right ── */
    dialog.uwc-drw--right {
      border-radius: var(--uwc-drawer-radius, ${t.radiusLg}) 0 0 var(--uwc-drawer-radius, ${t.radiusLg});
      border-right:  none;
    }
    dialog[open].uwc-drw--right:not(.is-closing) {
      animation: uwc-drw-in-right var(--uwc-drawer-duration, ${t.durationBase}) var(--uwc-easing, ${t.easingStandard}) both;
    }
    dialog[open].uwc-drw--right.is-closing {
      animation: uwc-drw-out-right var(--uwc-drawer-duration, ${t.durationBase}) ease both;
    }

    /* ── Top ── */
    dialog.uwc-drw--top {
      border-radius: 0 0 var(--uwc-drawer-radius, ${t.radiusLg}) var(--uwc-drawer-radius, ${t.radiusLg});
      border-top:    none;
    }
    dialog[open].uwc-drw--top:not(.is-closing) {
      animation: uwc-drw-in-top var(--uwc-drawer-duration, ${t.durationBase}) var(--uwc-easing, ${t.easingStandard}) both;
    }
    dialog[open].uwc-drw--top.is-closing {
      animation: uwc-drw-out-top var(--uwc-drawer-duration, ${t.durationBase}) ease both;
    }

    /* ── Bottom ── */
    dialog.uwc-drw--bottom {
      border-radius: var(--uwc-drawer-radius, ${t.radiusLg}) var(--uwc-drawer-radius, ${t.radiusLg}) 0 0;
      border-bottom: none;
    }
    dialog[open].uwc-drw--bottom:not(.is-closing) {
      animation: uwc-drw-in-bottom var(--uwc-drawer-duration, ${t.durationBase}) var(--uwc-easing, ${t.easingStandard}) both;
    }
    dialog[open].uwc-drw--bottom.is-closing {
      animation: uwc-drw-out-bottom var(--uwc-drawer-duration, ${t.durationBase}) ease both;
    }

    /* ── Full-screen ── (geometry also set inline; these handle visuals only) */
    dialog.uwc-drw--full {
      border-radius: 0 !important;
      border:        none !important;
    }

    /* ══════════════════════════════════════════════════════════════════════
       Header
    ══════════════════════════════════════════════════════════════════════ */
    .uwc-drw__header {
      display:         flex;
      align-items:     center;
      justify-content: space-between;
      gap:             ${t.space2};
      padding:         var(--uwc-drawer-header-padding, 0.875rem ${t.space4} 0.875rem ${t.space5});
      flex-shrink:     0;
      border-bottom:   1px solid var(--uwc-drawer-header-border, ${t.borderSubtle});
      background:      var(--uwc-drawer-header-bg, transparent);
      user-select:     none;
    }

    .uwc-drw__title {
      font-size:     var(--uwc-drawer-title-size, ${t.fontSizeLg});
      font-weight:   var(--uwc-drawer-title-weight, ${t.fontWeightSemibold});
      color:         var(--uwc-drawer-title-color, ${t.text});
      line-height:   1.25;
      flex:          1;
      min-width:     0;
      white-space:   nowrap;
      overflow:      hidden;
      text-overflow: ellipsis;
    }

    /* ══════════════════════════════════════════════════════════════════════
       Content / body
    ══════════════════════════════════════════════════════════════════════ */
    .uwc-drw__content {
      flex:        1;
      overflow-y:  auto;
      overflow-x:  hidden;
      padding:     var(--uwc-drawer-content-padding, ${t.space5});
      color:       var(--uwc-drawer-content-color, ${t.text});
      line-height: 1.6;

      scrollbar-width: thin;
      scrollbar-color: ${t.border} transparent;
    }
    .uwc-drw__content::-webkit-scrollbar       { width: 5px; }
    .uwc-drw__content::-webkit-scrollbar-thumb {
      background:    ${t.border};
      border-radius: ${t.radiusFull};
    }

    /* ══════════════════════════════════════════════════════════════════════
       Footer
    ══════════════════════════════════════════════════════════════════════ */
    .uwc-drw__footer {
      display:         flex;
      align-items:     center;
      justify-content: flex-end;
      gap:             ${t.space2};
      padding:         var(--uwc-drawer-footer-padding, 0.875rem ${t.space5});
      flex-shrink:     0;
      border-top:      1px solid var(--uwc-drawer-footer-border, ${t.borderSubtle});
      background:      var(--uwc-drawer-footer-bg, transparent);
    }

    .uwc-drw__footer--hide { display: none; }

    /* ══════════════════════════════════════════════════════════════════════
       Reduced motion
    ══════════════════════════════════════════════════════════════════════ */
    @media (prefers-reduced-motion: reduce) {
      dialog.uwc-drw,
      dialog.uwc-drw.is-closing,
      dialog.uwc-drw::backdrop,
      dialog.uwc-drw.is-closing::backdrop {
        animation: none;
      }
    }
  `,
];
